import type { Constructor, Container } from '@packages/core/container';

import { StsBackendClient } from '@packages/clients';
import { ANALYTICS_EVENTS, AnalyticsService } from '@packages/core/analytics';

import { StsBackendOperation } from '../../../sts-backend-operation';

/** Parameters for deleting a techline draft case. */
export type DeleteCaseParams = {
    /** The case number to delete. */
    caseNumber: string;
};

/**
 * Delete a techline case and log an analytics event on success.
 * The UI restricts this to draft cases; submitted cases follow a
 * different lifecycle (close, not delete).
 */
export class DeleteCase extends StsBackendOperation {
    /**
     * Register this operation with additional dependency on AnalyticsService.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- static `this` in an abstract class is not assignable to Constructor without a cast
        container.register(this as any as Constructor, [StsBackendClient, AnalyticsService]);
    }

    /** @param analyticsService - the analytics service for logging events */
    private analyticsService: AnalyticsService;

    /**
     * @param client - the backend API client
     * @param analyticsService - the analytics service for logging events
     */
    constructor(client: StsBackendClient, analyticsService: AnalyticsService) {
        super(client);
        this.analyticsService = analyticsService;
    }

    /**
     * Delete a draft case by case number.
     *
     * @param params - the case to delete
     * @returns true if the deletion succeeded
     */
    async exec(params: DeleteCaseParams): Promise<boolean> {
        const { caseNumber } = params;
        const response = await this.client.deleteCase({ caseNumber });

        if (response?.success) {
            this.analyticsService.logEvent(ANALYTICS_EVENTS.USER_DELETES_TECHLINE_DRAFT);
            return true;
        }

        return false;
    }
}
